<template>
  <div class="setup-page">
    <div class="setup-container">
      <header class="setup-header">
        <h1>IABF {{ festivalYear }} Setup Plan</h1>
        <div class="header-meta">
          <span class="progress-total">{{ totalDone }} / {{ totalCount }} done</span>
          <button
            v-if="volunteerName"
            type="button"
            class="name-btn"
            @click="promptForName"
            :title="`Checking off as ${volunteerName}`"
          >👤 {{ volunteerName }}</button>
          <button v-else type="button" class="name-btn" @click="promptForName">+ Add your name</button>
        </div>
      </header>

      <div class="save-banner">
        <span class="save-banner-icon">📦</span>
        <span class="save-banner-text">
          <strong>Save all boxes &amp; cores of the Marley rolls.</strong>
          Store backstage.
        </span>
      </div>

      <p class="setup-intro">
        This is a shared checklist for festival setup. Anyone with the link can check items off as
        they're done. If you uncheck something by mistake, just check it back.
      </p>

      <p v-if="loading" class="muted">Loading…</p>

      <p v-else-if="!rooms.length" class="muted">No setup tasks yet. An admin can add them at <code>/iabf/admin/setup-plan</code>.</p>

      <div v-else class="rooms-grid">
        <section v-for="room in rooms" :key="room.id" class="room-card">
          <header class="room-header">
            <h2>{{ room.name }}</h2>
            <span class="room-progress">{{ roomDoneCount(room) }} / {{ (room.items || []).length }}</span>
          </header>
          <p v-if="room.note" class="room-note">{{ room.note }}</p>
          <ul class="item-list">
            <li
              v-for="item in (room.items || [])"
              :key="item.id"
              :class="{ 'is-done': !!item.doneAt }"
            >
              <button
                type="button"
                class="check"
                :class="{ 'is-done': !!item.doneAt }"
                :disabled="busy === item.id"
                @click="onToggle(room, item)"
              >
                <span v-if="item.doneAt">✓</span>
              </button>
              <div class="item-body">
                <div class="item-text">{{ item.text }}</div>
                <a
                  v-if="item.imageUrl"
                  :href="item.imageUrl"
                  target="_blank"
                  rel="noopener"
                  class="item-image-link"
                >
                  <img :src="item.imageUrl" class="item-image" alt="" />
                </a>
                <div v-if="item.doneAt" class="item-meta">
                  <span v-if="item.doneBy">{{ item.doneBy }} · </span>{{ formatTime(item.doneAt) }}
                </div>
              </div>
            </li>
          </ul>
        </section>
      </div>
    </div>

    <!-- Uncheck confirmation -->
    <div v-if="confirmingUncheck" class="modal-overlay" @click.self="confirmingUncheck = null">
      <div class="modal-card">
        <h3>Mark this as not done?</h3>
        <p>"{{ confirmingUncheck.item.text }}" was checked off
          <span v-if="confirmingUncheck.item.doneBy">by {{ confirmingUncheck.item.doneBy }} </span>
          at {{ formatTime(confirmingUncheck.item.doneAt) }}.</p>
        <p>Uncheck it?</p>
        <div class="modal-actions">
          <button type="button" class="modal-btn modal-btn-secondary" @click="confirmingUncheck = null">Keep checked</button>
          <button type="button" class="modal-btn modal-btn-primary" @click="confirmUncheck">Yes, uncheck</button>
        </div>
      </div>
    </div>

    <!-- Name prompt -->
    <div v-if="namePromptOpen" class="modal-overlay" @click.self="closeNamePrompt">
      <div class="modal-card">
        <h3>Your name</h3>
        <p>Used to label items you check off so the team can see who did what.</p>
        <input
          ref="nameInput"
          v-model="nameDraft"
          type="text"
          class="name-input"
          placeholder="e.g. Sarah"
          @keydown.enter="saveName"
        />
        <div class="modal-actions">
          <button type="button" class="modal-btn modal-btn-secondary" @click="closeNamePrompt">Cancel</button>
          <button type="button" class="modal-btn modal-btn-primary" :disabled="!nameDraft.trim()" @click="saveName">Save</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { IABF_DANCER_YEAR } from '@/config/iabf-event'

const STORAGE_KEY = 'iabf-setup-volunteer-name'

export default {
  data () {
    return {
      loading: true,
      unsubscribe: null,
      volunteerName: '',
      namePromptOpen: false,
      nameDraft: '',
      pendingToggle: null,
      confirmingUncheck: null,
      busy: null
    }
  },
  computed: {
    festivalYear () {
      return Number(this.$store.getters.iabfDancerYear) ||
        Number(this.$store.getters.adminFestivalYear) ||
        IABF_DANCER_YEAR
    },
    rooms () {
      return (this.$store.getters.setupPlan && this.$store.getters.setupPlan.rooms) || []
    },
    totalCount () {
      let n = 0
      for (const r of this.rooms) n += (r.items || []).length
      return n
    },
    totalDone () {
      let n = 0
      for (const r of this.rooms) {
        for (const it of (r.items || [])) if (it.doneAt) n += 1
      }
      return n
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    try {
      this.volunteerName = String(window.localStorage.getItem(STORAGE_KEY) || '').trim()
    } catch (e) { /* noop */ }
    this.$store.dispatch('subscribeSetupPlan')
      .then(unsub => { this.unsubscribe = unsub })
      .catch(e => console.error('Subscribe failed:', e))
      .finally(() => { this.loading = false })
  },
  beforeDestroy () {
    if (typeof this.unsubscribe === 'function') this.unsubscribe()
  },
  methods: {
    roomDoneCount (room) {
      return (room.items || []).filter(i => !!i.doneAt).length
    },
    formatTime (ts) {
      if (!ts) return ''
      const d = new Date(ts)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    },
    onToggle (room, item) {
      if (item.doneAt) {
        this.confirmingUncheck = { room, item }
        return
      }
      if (!this.volunteerName) {
        this.pendingToggle = { room, item }
        this.openNamePrompt()
        return
      }
      this.commitToggle(room, item)
    },
    async confirmUncheck () {
      const { room, item } = this.confirmingUncheck
      this.confirmingUncheck = null
      await this.commitToggle(room, item)
    },
    async commitToggle (room, item) {
      this.busy = item.id
      try {
        await this.$store.dispatch('toggleSetupItem', {
          roomId: room.id,
          itemId: item.id,
          doneBy: this.volunteerName || null
        })
      } catch (e) {
        console.error('toggleSetupItem failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Could not save: ' + (e && e.message ? e.message : ''))
      }
      this.busy = null
    },
    promptForName () {
      this.openNamePrompt()
    },
    openNamePrompt () {
      this.nameDraft = this.volunteerName
      this.namePromptOpen = true
      this.$nextTick(() => {
        if (this.$refs.nameInput) this.$refs.nameInput.focus()
      })
    },
    closeNamePrompt () {
      this.namePromptOpen = false
      this.pendingToggle = null
    },
    async saveName () {
      const name = this.nameDraft.trim()
      if (!name) return
      this.volunteerName = name
      try { window.localStorage.setItem(STORAGE_KEY, name) } catch (e) { /* noop */ }
      this.namePromptOpen = false
      if (this.pendingToggle) {
        const { room, item } = this.pendingToggle
        this.pendingToggle = null
        await this.commitToggle(room, item)
      }
    }
  }
}
</script>

<style scoped>
.setup-page {
  background: #faf9f7;
  min-height: 100vh;
  padding: 32px 16px 80px;
  font-family: var(--iabf-font-body, inherit);
}

.setup-container {
  max-width: 1100px;
  margin: 0 auto;
}

.setup-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 32px;
  font-weight: 700;
  margin: 0;
  color: var(--iabf-ink, #1c1b1f);
}

.header-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-total {
  font-size: 14px;
  color: #555;
  background: #fff;
  border: 1px solid #e6e1d4;
  padding: 4px 10px;
  border-radius: 14px;
}

.name-btn {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
  border: none;
  font: inherit;
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
}

.name-btn:hover {
  background: #2f9695;
}

.save-banner {
  display: flex;
  align-items: center;
  gap: 18px;
  background: var(--iabf-secondary, #8e82a6);
  color: #fff;
  padding: 22px 28px;
  border-radius: 12px;
  margin: 12px 0 18px;
  box-shadow: 0 4px 14px rgba(221, 71, 98, 0.25);
}

.save-banner-icon {
  font-size: 44px;
  line-height: 1;
}

.save-banner-text {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 22px;
  line-height: 1.3;
}

.save-banner-text strong {
  display: block;
  font-size: 26px;
  margin-bottom: 2px;
}

@media (max-width: 540px) {
  .save-banner {
    padding: 18px 20px;
  }
  .save-banner-icon {
    font-size: 36px;
  }
  .save-banner-text {
    font-size: 18px;
  }
  .save-banner-text strong {
    font-size: 21px;
  }
}

.setup-intro {
  color: #555;
  margin: 0 0 24px;
  max-width: 720px;
}

.muted {
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
}

.rooms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.room-card {
  background: #fff;
  border: 1px solid #e6e1d4;
  border-radius: 8px;
  padding: 14px 16px;
}

.room-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.room-header h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  color: var(--iabf-ink, #1c1b1f);
}

.room-progress {
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
}

.room-note {
  font-size: 13px;
  color: #5d4037;
  font-style: italic;
  margin: 0 0 6px;
  padding: 4px 8px;
  background: #fbf8f3;
  border-radius: 4px;
}

.item-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.item-list li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid #f1eee9;
}

.item-list li:last-child {
  border-bottom: none;
}

.check {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: 1.5px solid #c0bba7;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}

.check.is-done {
  background: var(--iabf-primary, #8a957d);
  border-color: var(--iabf-primary, #8a957d);
}

.check:hover {
  border-color: var(--iabf-primary, #8a957d);
}

.item-body {
  flex: 1;
  min-width: 0;
}

.item-text {
  font-size: 14px;
  color: var(--iabf-ink, #1c1b1f);
  line-height: 1.4;
}

li.is-done .item-text {
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: line-through;
}

.item-image-link {
  display: inline-block;
  margin-top: 6px;
}

.item-image {
  max-width: 220px;
  max-height: 180px;
  border-radius: 4px;
  border: 1px solid #e6e1d4;
  display: block;
}

.item-meta {
  font-size: 11px;
  color: var(--iabf-primary, #8a957d);
  margin-top: 2px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-card {
  background: #fff;
  border-radius: 8px;
  padding: 22px 24px;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.modal-card h3 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 18px;
  margin: 0 0 8px;
}

.modal-card p {
  margin: 0 0 10px;
  color: #555;
  font-size: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

.modal-btn {
  font: inherit;
  font-size: 14px;
  padding: 8px 14px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
}

.modal-btn-secondary {
  background: #f1eee9;
  color: #555;
}

.modal-btn-primary {
  background: var(--iabf-secondary, #8e82a6);
  color: #fff;
}

.modal-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.name-input {
  width: 100%;
  padding: 8px 10px;
  font: inherit;
  font-size: 15px;
  border: 1px solid #d0cbc4;
  border-radius: 4px;
}

.name-input:focus {
  border-color: var(--iabf-primary, #8a957d);
  outline: none;
}
</style>
</content>
</invoke>